import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

export const CHUNK_RELOAD_SESSION_KEY = 'qbc-chunk-reload'

/** Clear after a successful full page load so a future deploy can trigger one reload again. */
export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY)
  } catch {
    // ignore private mode / blocked storage
  }
}

export function isChunkLoadError(error: unknown): boolean {
  if (typeof error === 'string') {
    return error.toLowerCase().includes('failed to fetch dynamically imported module')
  }

  if (!(error instanceof Error)) {
    return false
  }

  const msg = error.message.toLowerCase()

  return (
    error.name === 'ChunkLoadError' ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('dynamically imported module')
  )
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory()
    } catch (error) {
      if (isChunkLoadError(error)) {
        reloadOnceForStaleChunk()
      }
      throw error
    }
  })
}

function reloadOnceForStaleChunk(): void {
  try {
    if (!sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1')
      window.location.reload()
      return
    }
  } catch {
    window.location.reload()
  }
}
